import { useMemo } from 'react';

import path from 'path';

import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import useSWR from 'swr';

import ErrorPage from '~components/errorBoundary/errorPage';
import ErrorType from '~components/errorBoundary/errorType';
import { pagePathSegments } from '~constants';
import { SelectedDevicesProvider } from '~features/devices';
import { SelectedKeysProvider } from '~features/keys';
import OrganisationProvider from '~features/organisations/providers/organisationDetailsProvider';
import OrganisationsService from '~features/organisations/services/organisationsService';
import { OrganisationScheduledActionsContainer } from '~features/scheduledActions';
import PageLayout from '~layouts/page/pageLayout';
import { getService } from '~services/serviceFactory';

import OrganisationDetailsContainer from '../containers/organisationDetailsContainer';
import OrganisationDevicesContainer from '../containers/organisationDevicesContainer';
import OrganisationKeysContainer from '../containers/organisationKeysContainer';
import OrganisationResourceGroupsContainer from '../containers/organisationResourceGroupsContainer';
import OrganisationUsersContainer from '../containers/organisationUsersContainer';

interface OrganisationDetailsProps {
	tab: 'general' | 'users' | 'resource-groups' | 'devices' | 'keys' | 'scheduled-actions';
}

const organisationsService = getService(OrganisationsService);

const OrganisationDetailsPage = ({ tab }: OrganisationDetailsProps) => {
	const { t } = useTranslation();
	const { id: organisationId } = useParams();

	const {
		data: organisation,
		isLoading,
		error: organisationError,
	} = useSWR(organisationsService.getOrganisationByIdCacheKey(organisationId), () =>
		organisationsService.getOrganisationById(organisationId!)
	);

	const tabs = useMemo(
		() =>
			organisationId ?
				[
					{
						key: pagePathSegments.General,
						title: t('general.generalInformation', { ns: 'entities' }),
						path: path.join(
							'/',
							pagePathSegments.Organisations,
							organisationId,
							pagePathSegments.General
						),
						component: <OrganisationDetailsContainer />,
					},
					{
						key: pagePathSegments.Users,
						title: t('user.labels.plural', { ns: 'entities' }),
						path: path.join(
							'/',
							pagePathSegments.Organisations,
							organisationId,
							pagePathSegments.Users
						),
						component: <OrganisationUsersContainer />,
					},
					{
						key: pagePathSegments.ResourceGroups,
						title: t('resourceGroup.labels.plural', { ns: 'entities' }),
						path: path.join(
							'/',
							pagePathSegments.Organisations,
							organisationId,
							pagePathSegments.ResourceGroups
						),
						component: <OrganisationResourceGroupsContainer />,
					},
					{
						key: pagePathSegments.Devices,
						title: t('device.labels.plural', { ns: 'entities' }),
						path: path.join(
							'/',
							pagePathSegments.Organisations,
							organisationId,
							pagePathSegments.Devices
						),
						component: (
							<SelectedDevicesProvider>
								<OrganisationDevicesContainer />
							</SelectedDevicesProvider>
						),
					},
					{
						key: pagePathSegments.Keys,
						title: t('key.labels.plural', { ns: 'entities' }),
						path: path.join(
							'/',
							pagePathSegments.Organisations,
							organisationId,
							pagePathSegments.Keys
						),
						component: (
							<SelectedKeysProvider>
								<OrganisationKeysContainer />
							</SelectedKeysProvider>
						),
					},
					{
						key: pagePathSegments.ScheduledActions,
						title: t('scheduledAction.labels.plural', { ns: 'entities' }),
						path: path.join(
							'/',
							pagePathSegments.Organisations,
							organisationId,
							pagePathSegments.ScheduledActions
						),
						component: <OrganisationScheduledActionsContainer />,
					},
				]
			:	[],
		[organisationId]
	);

	const crumbs = useMemo(
		() => [
			{
				title: t('organisation.labels.plural', { ns: 'entities' }),
				path: path.join('/', pagePathSegments.Organisations),
			},
			{
				title: organisation?.name ?? organisationId ?? '',
				path: '',
			},
		],
		[organisation]
	);

	const computedTitle = useMemo(
		() =>
			organisation?.name ??
			`${t('organisation.labels.single', { ns: 'entities' })} ${organisationId ?? ''}`,
		[organisation]
	);

	// --- All hooks above, all early returns below ---

	if (organisationId == undefined) {
		return <Navigate to={pagePathSegments.Organisations} replace />;
	}

	if (!isLoading && organisationError) {
		return <ErrorPage type={ErrorType.NotFound} />;
	}

	return (
		<OrganisationProvider value={{ isLoading: isLoading, organisation: organisation }}>
			<PageLayout
				isLoading={isLoading}
				pageTitle={computedTitle}
				headerInfo={{
					title: computedTitle,
				}}
				crumbs={crumbs}
				tabs={tabs}
				activeTabKey={tab}
			/>
		</OrganisationProvider>
	);
};

export default OrganisationDetailsPage;
